// Libs
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
// Hooks
import useOrderApi from 'core/hooks/useCoreApi';
// Components
import {
	Wrapper,
	Divider,
	PersonIcon,
	Span,
	LogoIcon,
	Button,
	Empty,
} from 'components';
import { ApiServiceV2 } from 'services';
// Utils
import { VirtualizedList } from 'screens/Utils';
import orderBy from 'utils/orderBy';
import { formatPaymentsToPaycutReport } from 'screens/Billings/utils';
import { coinsUtils } from 'utils/libs';
// selectors
import { selectProfile } from 'screens/Login/selectors';
// Actions
import { sendToast } from 'screens/Dashboard/actions';

function GetResumeItem({ concept, value, color }) {
	const theme = useTheme();

	return (
		<Wrapper padding='5px' justifyContent='space-between' width='95%'>
			<Span fontSize='l' color={color || theme.fonts.colors.concept}>
				{concept}
			</Span>
			<Span fontSize='l' color={color || theme.fonts.colors.value}>
				{value}
			</Span>
		</Wrapper>
	);
}

const ResumenSection = ({ totals, theme }) => {
	return (
		<>
			<Divider color='gray'>Resumen</Divider>
			<Wrapper flexDirection='column' width='100%'>
				<GetResumeItem concept='Ordenes' value={totals.orders_count} />
				<GetResumeItem concept='Producción' value={totals.production} />
				<GetResumeItem concept='Créditos' value={totals.credits} />
				<GetResumeItem concept='Débitos' value={totals.discounts} />
				<GetResumeItem concept='Subtotal' value={totals.subtotal} />
				<GetResumeItem concept='IVA' value={`${totals.iva}%`} />
				<GetResumeItem concept='Impuestos' value={totals.subtotal_with_iva} />
				<GetResumeItem
					concept='Total a Pagar'
					value={totals.total}
					color={theme.fonts.colors.emphasis_1}
				/>
			</Wrapper>
		</>
	);
};

const SignedSeccion = ({ signed, profile, signedUsers, theme }) => {
	orderBy(signed, 'objectDate', {
		objectKey: 'created_at',
		dateFormat: profile?.user?.settings?.date_format,
	});

	return (
		<>
			<Divider color='gray'>Firmas</Divider>
			<Wrapper
				width='100%'
				margin='20px 0 10px 0'
				borderRadius='20px'
				backgroundColor={theme.colors.secondary[800]}
				overflow='auto'
			>
				{signed.map((sign, idx) => {
					const user = signedUsers.find(
						user => String(user.id) === String(sign.user_id),
					);
					if (!user) return null;

					let style = { color: 'rgba(255, 255, 255, 0.6)' };

					if (sign.paid) style = { color: 'green' };
					else if (sign.released) style = { color: 'yellow' };
					else if (sign.agreed) style = { color: 'deepskyblue' };

					return (
						user && (
							<Wrapper key={idx} padding='0px'>
								<PersonIcon style={style} />
								<Wrapper
									margin='0 10px 10px 0'
									padding='0px'
									flexDirection='column'
									alignItems='baseline'
								>
									<Span style={style} margin='0 10px'>
										{user.name} ({sign.paycut_id})
									</Span>
									<Span margin='0 10px' color='cadetblue'>
										{sign.created_at}
									</Span>
								</Wrapper>
							</Wrapper>
						)
					);
				})}
			</Wrapper>
		</>
	);
};

const FooterSeccion = ({ state, handdleSignedTransaction }) => {
	return (
		<Wrapper width='100%' justifyContent='flex-end'>
			{state.isLoading ? (
				<LogoIcon button spin />
			) : (
				<Button mode='primary' onClick={handdleSignedTransaction}>
					Firmar
				</Button>
			)}
		</Wrapper>
	);
};

const ResumeBody = ({
	paycut = {},
	pays = [],
	paycuts = [],
	coinId,
	cleanModal,
	refreshMainData,
	paycutIds,
}) => {
	const targetRef = useRef();

	const [state, _setState] = useState({
		isLoading: false,
		signedUsers: [],
		coinSymbol: {},
	});

	const setState = newState =>
		_setState(current => ({ ...current, ...newState }));

	const theme = useTheme();
	const profile = useSelector(selectProfile);
	const { getCoinsData } = useOrderApi();

	const totals = formatPaymentsToPaycutReport(pays, state.coinSymbol);

	const signed = paycuts.reduce((acc, pc) => {
		if (Array.isArray(pc.signed)) acc = acc.concat(pc.signed);
		return acc;
	}, []);

	const recordsHaveAllowedStates = (state, allowStates) =>
		!!allowStates.find(allowState => allowState === state);

	const isPaycutReady = recordsHaveAllowedStates(paycut.state, [
		'ready',
		'released',
	]);

	const getSignedUsers = signedUserIds => {
		ApiServiceV2.post({
			url: `/users/getUsers`,
			data: { usersId: [...new Set(signedUserIds)] },
		})
			.then(users => {
				setState({ signedUsers: Array.isArray(users) ? users : [] });
			})
			.catch(err => {
				sendToast({ message: err.message, type: err.type });
			});
	};

	useEffect(() => {
		const signedUserIds = signed.map(s => s.user_id);
		if (signedUserIds.length === 0) return;
		getSignedUsers(signedUserIds);
	}, [paycuts]);

	useEffect(() => {
		getCoinsData().then(coinsData => {
			const coinSymbol = coinsUtils.getDataByCoinId(
				coinId,
				'symbol',
				coinsData,
			);

			setState({
				coinSymbol,
			});
		});
	}, [coinId]);

	const handdleSignedTransaction = () => {
		ApiServiceV2.put({
			url: `/paycuts/sign`,
			data: { paycutIds },
		});

		cleanModal();
		refreshMainData();
	};

	return (
		<Wrapper padding='0' width='100%' flexDirection='column' overflow='auto'>
			<Wrapper
				elementRef={targetRef}
				width='100%'
				flexDirection='column'
				justifyContent='space-between'
			>
				{/* PAYMENTS */}
				<Divider color='gray'>Pagos</Divider>
				<Wrapper>
					{pays.length === 0 ? (
						<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
					) : (
						<VirtualizedList
							cardId='paycutResumeCard'
							listStyle={{
								height: document.documentElement.clientHeight * 0.5,
								width: targetRef.current
									? targetRef.current.offsetWidth
									: 0,
							}}
							rowHeight={200}
							spinTitle='Consultando registros...'
							rows={pays}
							customProps={{
								coin_symbol: state.coinSymbol,
							}}
						/>
					)}
				</Wrapper>

				{/* RESUME */}
				<ResumenSection totals={totals} theme={theme} />

				{/* SIGNED */}
				<SignedSeccion
					signed={signed}
					profile={profile}
					signedUsers={state.signedUsers}
					theme={theme}
				/>

				{isPaycutReady && (
					<FooterSeccion
						state={state}
						handdleSignedTransaction={handdleSignedTransaction}
					/>
				)}
			</Wrapper>
		</Wrapper>
	);
};

export default ResumeBody;
